import { useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, IS_PREVIEW } from "@/lib/api";
import { fmt, fmtDate } from "@/lib/format";
import InstallPWA from "@/components/InstallPWA";
import { initCardify } from "@/lib/cardify";
import GlobalSearch from "@/components/GlobalSearch";
import KilavuzButton from "@/components/KilavuzButton";
import { LayoutDashboard, Table2, LogOut, TrendingUp, CalendarClock, Wallet, Users, CalendarDays, Bell, ClipboardList, Truck, ShieldCheck, History, ListChecks, BarChart3, Menu, X, Gavel, Phone, Scale, Receipt, Coins, Settings, Bug } from "lucide-react";

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [alerts, setAlerts] = useState({ overdue: [], due_soon: [], count: 0 });
  const [promiseAlerts, setPromiseAlerts] = useState({ overdue: [], due_soon: [], count: 0 });
  const [myTasks, setMyTasks] = useState(0);
  const [promiseApprovals, setPromiseApprovals] = useState(0);
  const [hacizCount, setHacizCount] = useState(0);
  const [hazirCount, setHazirCount] = useState(0);
  const [alertOpen, setAlertOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [hataOpen, setHataOpen] = useState(false);
  const [hataMsg, setHataMsg] = useState("");
  const [hataTur, setHataTur] = useState("hata");
  const [hataBusy, setHataBusy] = useState(false);
  const [hataList, setHataList] = useState([]);
  const [hataBenim, setHataBenim] = useState([]);
  const [hataUnread, setHataUnread] = useState(0);
  const [cozumInputs, setCozumInputs] = useState({});
  const isAdmin = user?.role === "admin";
  const loadHata = () => {
    if (user?.role !== "admin") return;
    api.get("/hata-rapor/sayi").then((r) => setHataUnread(r.data.okunmamis || 0)).catch(() => {});
  };
  useEffect(() => { loadHata(); }, [user?.role]);
  const openHata = async () => {
    setHataOpen(true);
    try { const bm = await api.get("/hata-rapor/benim"); setHataBenim(bm.data || []); } catch (e) { /* ignore */ }
    if (isAdmin) {
      try {
        const { data } = await api.get("/hata-rapor");
        setHataList(data || []);
        await api.post("/hata-rapor/okundu");
        setHataUnread(0);
      } catch (e) { /* ignore */ }
    }
  };
  const submitHata = async () => {
    if (!hataMsg.trim()) { toast.error("Lütfen mesajı yazın"); return; }
    setHataBusy(true);
    try {
      await api.post("/hata-rapor", { mesaj: hataMsg, sayfa: window.location.pathname, tur: hataTur });
      toast.success(hataTur === "oneri" ? "Öneriniz iletildi, teşekkürler" : "Hata bildiriminiz iletildi, teşekkürler");
      setHataMsg("");
      try { const bm = await api.get("/hata-rapor/benim"); setHataBenim(bm.data || []); } catch (e) { /* ignore */ }
      if (isAdmin) { try { const { data } = await api.get("/hata-rapor"); setHataList(data || []); } catch (e) { /* ignore */ } }
    } catch (e) { toast.error("Bildirim gönderilemedi"); }
    finally { setHataBusy(false); }
  };
  const resolveHata = async (id, tur) => {
    const not = (cozumInputs[id] || "").trim();
    try {
      await api.put(`/hata-rapor/${id}/durum`, { durum: "cozuldu", cozum_notu: not });
      toast.success(tur === "oneri" ? "Öneri 'yapıldı' olarak işaretlendi" : "Hata 'çözüldü' olarak işaretlendi");
      const { data } = await api.get("/hata-rapor"); setHataList(data || []);
      setCozumInputs((p) => ({ ...p, [id]: "" }));
    } catch (e) { toast.error("Güncellenemedi"); }
  };
  const hataTurBadge = (h) => (
    <span className={`px-1.5 py-0.5 rounded-sm text-[10px] font-semibold ${h.tur === "oneri" ? "bg-[#3B6FB0]/15 text-[#3B6FB0]" : "bg-[#A63A3A]/15 text-[#A63A3A]"}`}>{h.tur === "oneri" ? "ÖNERİ" : "HATA"}</span>
  );
  const hataDurumBadge = (h) => (
    h.durum === "cozuldu"
      ? <span className="px-1.5 py-0.5 rounded-sm text-[10px] font-semibold bg-[#2F6E4E]/15 text-[#2F6E4E]">{h.tur === "oneri" ? "YAPILDI" : "ÇÖZÜLDÜ"}</span>
      : <span className="px-1.5 py-0.5 rounded-sm text-[10px] font-semibold bg-[#B8933F]/15 text-[#B8933F]">BEKLİYOR</span>
  );
  const isKatip = user?.role === "katip";
  const totalAlerts = isKatip ? (hacizCount + myTasks) : (alerts.count + promiseAlerts.count);

  useEffect(() => {
    if (user?.role === "admin" || user?.role === "user") {
      api.get("/cari/alerts").then((res) => setAlerts(res.data)).catch(() => {});
      api.get("/promises/alerts").then((res) => setPromiseAlerts(res.data)).catch(() => {});
    }
  }, [user?.role]);

  useEffect(() => {
    if (!user?.email || user?.role === "katip") return;
    api.get("/applications", { params: { atanan: user.email } }).then((res) => {
      const pending = (res.data || []).filter((a) => a.durum === "Beklemede" || a.durum === "Admin Onayında");
      setMyTasks(pending.length);
      if (pending.length > 0) toast.info(`Bugün size atanmış ${pending.length} başvuru var`, { id: "mytasks" });
    }).catch(() => {});
  }, [user?.email]);

  useEffect(() => {
    if (user?.role === "admin") {
      api.get("/cari/odeme-sozu-onaylar").then((r) => setPromiseApprovals((r.data || []).length)).catch(() => {});
    }
    if (user?.role === "admin" || user?.role === "katip") {
      Promise.all([
        api.get("/haciz-gundemi/adaylar").then((r) => (r.data || []).length).catch(() => 0),
        api.get("/haciz-gundemi/alarm").then((r) => r.data?.count || 0).catch(() => 0),
      ]).then(([a, al]) => setHacizCount(a + al));
    }
    if (user?.role === "katip") {
      api.get("/haciz-gundemi/gorevlerim").then((r) => setMyTasks((r.data || []).length)).catch(() => {});
    }
    if (user?.role === "avukat") {
      api.get("/haciz-gundemi/hazir").then((r) => setHazirCount((r.data || []).length)).catch(() => {});
    }
  }, [user?.role]);

  useEffect(() => initCardify(), []);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") setMenuOpen(false); };
    if (menuOpen) {
      window.addEventListener("keydown", onKey);
      document.body.style.overflow = "hidden";
    }
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  const drawerLinkClass = ({ isActive }) =>
    `flex items-center gap-3.5 md:gap-3 px-4 py-3 md:py-2.5 rounded-sm text-base md:text-sm font-medium transition-colors duration-150 ${
      isActive
        ? "bg-[#26231E] text-[#F3E9CF]"
        : "text-[#4A443B] hover:bg-[#EFE7D6] hover:text-[#26231E]"
    }`;

  let navGroups = [];
  if (user?.role === "izleyici") {
    navGroups = [{ title: null, items: [{ to: "/ozet", label: "Özet Panel", icon: BarChart3, testid: "nav-ozet" }] }];
  } else if (user?.role === "katip") {
    navGroups = [
      {
        title: "İcra",
        items: [
          { to: "/kayitlar", label: "İcra Kayıtları", icon: Table2, testid: "nav-records" },
          { to: "/haciz-gundemi", label: "Haciz Gündemi", icon: Scale, testid: "nav-haciz-gundemi", badge: hacizCount },
        ],
      },
      { title: "İl Dışı", items: [{ to: "/il-disi-harc", label: "İl Dışı Harç & Masraf", icon: Receipt, testid: "nav-ildisi-harc" }] },
      { title: "Görevler", items: [{ to: "/gorevlerim", label: "Görevlerim", icon: ListChecks, testid: "nav-gorevlerim", badge: myTasks }] },
    ];
  } else if (user?.role === "avukat") {
    navGroups = [
      { title: "İcra", items: [
        { to: "/kayitlar", label: "İcra Kayıtları", icon: Table2, testid: "nav-records" },
        { to: "/haciz-gundemi", label: "Haciz Gündemi", icon: Scale, testid: "nav-haciz-gundemi" },
      ] },
      { title: "Görevler", items: [{ to: "/avukat-gorevlerim", label: "Görevlerim", icon: ListChecks, testid: "nav-avukat-gorevlerim", badge: hazirCount }] },
      { title: "İl Dışı", items: [{ to: "/il-disi-harc", label: "İl Dışı Harç & Masraf", icon: Receipt, testid: "nav-ildisi-harc" }] },
    ];
  } else {
    const icra = [];
    if (user?.role === "admin") {
      icra.push({ to: "/", label: "İcra Paneli", icon: LayoutDashboard, end: true, testid: "nav-dashboard" });
      icra.push({ to: "/kayitlar", label: "İcra Kayıtları", icon: Table2, testid: "nav-records" });
      icra.push({ to: "/icra-tahsilat-rapor", label: "Tahsilat Raporu", icon: Wallet, testid: "nav-tahsilat-rapor" });
      if (IS_PREVIEW) icra.push({ to: "/icra-mutabakat", label: "İl Dışı Mutabakat", icon: BarChart3, testid: "nav-icra-mutabakat" });
      icra.push({ to: "/haciz-gundemi", label: "Haciz Gündemi", icon: Scale, testid: "nav-haciz-gundemi", badge: hacizCount });
    }
    if (icra.length) navGroups.push({ title: "İcra", items: icra });
    navGroups.push({
      title: "Cari",
      items: [
        ...(user?.role === "admin" ? [{ to: "/cari-panel", label: "Cari Paneli", icon: Wallet, testid: "nav-cari-panel" }] : []),
        { to: "/cari", label: "Cari Takip", icon: Users, end: true, testid: "nav-cari" },
        { to: "/cari-vadeler", label: "Cari Vadeleri", icon: CalendarDays, testid: "nav-cari-vadeler" },
        ...(user?.role === "admin" ? [{ to: "/odeme-sozleri", label: "Ödeme Sözleri", icon: Phone, testid: "nav-odeme-sozleri" }] : []),
        ...(user?.role === "admin" ? [{ to: "/icralik", label: "İcralık Onayı", icon: Gavel, testid: "nav-icralik" }] : []),
      ],
    });
    navGroups.push({
      title: "Operasyon",
      items: [
        { to: "/basvurular", label: "Ön Onay Başvuruları", icon: ClipboardList, testid: "nav-basvurular" },
        { to: "/gorevlerim", label: "Görevlerim", icon: ListChecks, testid: "nav-gorevlerim", badge: myTasks },
        { to: "/sevkiyat", label: "Sevkiyat Planı", icon: Truck, testid: "nav-sevkiyat" },
      ],
    });
    navGroups.push({
      title: "İl Dışı",
      items: [
        { to: "/il-disi-harc", label: "İl Dışı Harç & Masraf", icon: Receipt, testid: "nav-ildisi-harc" },
        ...(user?.role === "admin" ? [{ to: "/il-disi-kasa", label: "Kasa", icon: Wallet, testid: "nav-ildisi-kasa" }] : []),
      ],
    });
    if (user?.role === "admin") {
      navGroups.push({
        title: "Altın",
        items: [
          { to: "/altin-panel", label: "Altın Paneli", icon: Coins, testid: "nav-altin-panel" },
          { to: "/altin-cari", label: "Altın Cari", icon: Coins, testid: "nav-altin-cari" },
          { to: "/altin-muhasebe", label: "Altın Muhasebe", icon: BarChart3, testid: "nav-altin-muhasebe" },
        ],
      });
      navGroups.push({
        title: "Yönetim",
        items: [
          { to: "/kullanicilar", label: "Kullanıcılar", icon: ShieldCheck, testid: "nav-users" },
          { to: "/odeme-sozu-onaylar", label: "Ödeme Sözü Onayları", icon: Phone, testid: "nav-promise-approvals", badge: promiseApprovals },
          { to: "/silme-kaydi", label: "Silme Kaydı", icon: History, testid: "nav-audit" },
          { to: "/denetim", label: "Personel Denetimi", icon: ClipboardList, testid: "nav-denetim" },
          { to: "/ozet", label: "Özet Panel", icon: BarChart3, testid: "nav-ozet" },
        ],
      });
    }
  }

  return (
    <div className="min-h-screen bg-[#F6F0E6]">
      <header className="sticky top-0 z-50 bg-[#FFFDF9] border-b border-[#E7DECC]">
        <div className="flex items-center justify-between px-4 sm:px-6">
          <div className="flex items-center gap-3 min-w-0">
            <button
              onClick={() => setMenuOpen(true)}
              data-testid="menu-toggle"
              aria-label="Menüyü aç"
              className="p-2 -ml-2 rounded-sm hover:bg-[#EFE7D6] transition-colors duration-150"
            >
              <Menu className="w-6 h-6 text-[#26231E]" />
            </button>
            <div className="flex items-center gap-2 py-3 shrink-0">
              <img src="/logo-tek.jpg" alt="Tek Hukuk" className="h-8 w-auto" style={{ mixBlendMode: "multiply" }} />
            </div>
            <GlobalSearch />
          </div>
          <div className="flex items-center gap-1 sm:gap-4 shrink-0">
            <div className="relative">
              <button
                onClick={() => setAlertOpen((o) => !o)}
                data-testid="alerts-bell"
                aria-label="Vade uyarıları"
                className="relative p-2 rounded-sm hover:bg-[#EFE7D6] transition-colors duration-150"
              >
                <Bell className="w-5 h-5 text-[#8C8377]" />
                {totalAlerts > 0 && (
                  <span data-testid="alerts-count" className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#A63A3A] text-white text-[10px] font-bold flex items-center justify-center">
                    {totalAlerts}
                  </span>
                )}
              </button>
              {alertOpen && (
                <div className="fixed left-3 right-3 top-[60px] w-auto max-w-none sm:absolute sm:left-auto sm:right-0 sm:top-auto sm:mt-2 sm:w-80 bg-white border border-[#E7DECC] rounded-lg shadow-xl z-[60]" data-testid="alerts-dropdown">
                  <div className="px-4 py-3 border-b border-[#E7DECC] flex items-center justify-between">
                    <span className="font-heading font-bold text-sm tracking-tight">Uyarılar</span>
                    {!isKatip && <button onClick={() => { setAlertOpen(false); navigate("/cari-vadeler"); }} className="text-xs text-[#B8933F] font-medium">Vadeler</button>}
                  </div>
                  <div className="max-h-96 overflow-y-auto" data-testid="alerts-list">
                    {isKatip ? (
                      <>
                        <button onClick={() => { setAlertOpen(false); navigate("/gorevlerim"); }} data-testid="katip-alert-gorevlerim" className="w-full flex items-center justify-between px-4 py-3 hover:bg-[#EFE7D6] border-b border-[#E7DECC] text-left transition-colors duration-150">
                          <span className="flex items-center gap-2 text-sm font-medium"><ListChecks className="w-4 h-4 text-[#B8933F]" /> Görevlerim</span>
                          {myTasks > 0 && <span className="min-w-[18px] h-[18px] px-1 rounded-full bg-[#B8933F] text-white text-[10px] font-bold flex items-center justify-center">{myTasks}</span>}
                        </button>
                        <button onClick={() => { setAlertOpen(false); navigate("/haciz-gundemi"); }} data-testid="katip-alert-haciz" className="w-full flex items-center justify-between px-4 py-3 hover:bg-[#EFE7D6] text-left transition-colors duration-150">
                          <span className="flex items-center gap-2 text-sm font-medium"><Scale className="w-4 h-4 text-[#B8933F]" /> Haciz Gündemi</span>
                          {hacizCount > 0 && <span className="min-w-[18px] h-[18px] px-1 rounded-full bg-[#B8933F] text-white text-[10px] font-bold flex items-center justify-center">{hacizCount}</span>}
                        </button>
                      </>
                    ) : totalAlerts === 0 ? (
                      <div className="px-4 py-6 text-center text-sm text-[#8C8377]">Uyarı yok.</div>
                    ) : (
                      <>
                        {alerts.count > 0 && (
                          <div className="px-4 py-1.5 bg-[#F3ECDD] kicker border-b border-[#E7DECC]">CARİ VADELERİ</div>
                        )}
                        {alerts.overdue.map((a) => (
                          <button key={a.id} onClick={() => { setAlertOpen(false); navigate("/cari"); }} className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-[#EFE7D6] border-b border-[#E7DECC] text-left transition-colors duration-150">
                            <div>
                              <div className="text-sm font-medium">{a.musteri}</div>
                              <div className="text-xs text-[#A63A3A] font-semibold">{Math.abs(a.gun)} gün gecikmiş · {fmtDate(a.vade_tarihi)}</div>
                            </div>
                            <span className="text-sm font-bold tabular-nums text-[#A63A3A]">{fmt(a.kalan)}</span>
                          </button>
                        ))}
                        {alerts.due_soon.map((a) => (
                          <button key={a.id} onClick={() => { setAlertOpen(false); navigate("/cari"); }} className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-[#EFE7D6] border-b border-[#E7DECC] text-left transition-colors duration-150">
                            <div>
                              <div className="text-sm font-medium">{a.musteri}</div>
                              <div className="text-xs text-[#B8933F] font-semibold">{a.gun === 0 ? "Bugün" : `${a.gun} gün kaldı`} · {fmtDate(a.vade_tarihi)}</div>
                            </div>
                            <span className="text-sm font-bold tabular-nums text-[#B8933F]">{fmt(a.kalan)}</span>
                          </button>
                        ))}
                        {promiseAlerts.count > 0 && (
                          <div className="px-4 py-1.5 bg-[#F3ECDD] kicker border-b border-[#E7DECC]" data-testid="promise-alerts-header">ÖDEME SÖZÜ TAAHHÜTLERİ</div>
                        )}
                        {promiseAlerts.overdue.map((a, i) => (
                          <button key={`po-${i}`} onClick={() => { setAlertOpen(false); navigate(a.kaynak === "İcra" ? `/icra/${a.rid}` : "/cari-panel"); }} className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-[#EFE7D6] border-b border-[#E7DECC] text-left transition-colors duration-150">
                            <div>
                              <div className="text-sm font-medium">{a.musteri} <span className="text-xs text-[#8C8377]">({a.kaynak})</span></div>
                              <div className="text-xs text-[#A63A3A] font-semibold">{Math.abs(a.gun)} gün gecikmiş taahhüt · {fmtDate(a.taahhut_tarihi)}</div>
                            </div>
                            {a.taahhut_tutari ? <span className="text-sm font-bold tabular-nums text-[#A63A3A]">{fmt(a.taahhut_tutari)}</span> : null}
                          </button>
                        ))}
                        {promiseAlerts.due_soon.map((a, i) => (
                          <button key={`pd-${i}`} onClick={() => { setAlertOpen(false); navigate(a.kaynak === "İcra" ? `/icra/${a.rid}` : "/cari-panel"); }} className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-[#EFE7D6] border-b border-[#E7DECC] text-left transition-colors duration-150">
                            <div>
                              <div className="text-sm font-medium">{a.musteri} <span className="text-xs text-[#8C8377]">({a.kaynak})</span></div>
                              <div className="text-xs text-[#B8933F] font-semibold">{a.gun === 0 ? "Bugün taahhüt" : `${a.gun} gün kaldı`} · {fmtDate(a.taahhut_tarihi)}</div>
                            </div>
                            {a.taahhut_tutari ? <span className="text-sm font-bold tabular-nums text-[#B8933F]">{fmt(a.taahhut_tutari)}</span> : null}
                          </button>
                        ))}
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
            <div className="text-right hidden sm:block">
              <div className="text-sm font-medium leading-tight" data-testid="user-name">
                {user?.name}
              </div>
              <div className="text-xs text-[#8C8377]">{user?.email}</div>
            </div>
            <InstallPWA />
            <KilavuzButton />
            <button
              onClick={openHata}
              data-testid="hata-bildir-button"
              aria-label="Hata Bildir"
              className="relative flex items-center gap-1.5 px-2 sm:px-3 py-2 text-sm text-[#8C8377] hover:text-[#A63A3A] hover:bg-[#EFE7D6] rounded-sm transition-colors duration-150"
            >
              <Bug className="w-4 h-4" /> <span className="hidden sm:inline">Hata Bildir</span>
              {isAdmin && hataUnread > 0 && (
                <span data-testid="hata-unread-badge" className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-[#A63A3A] text-white text-[10px] flex items-center justify-center">{hataUnread}</span>
              )}
            </button>
            <NavLink
              to="/hesap-ayarlari"
              data-testid="nav-account-settings"
              className="flex items-center gap-1.5 px-2 sm:px-3 py-2 text-sm text-[#8C8377] hover:text-[#B8933F] hover:bg-[#EFE7D6] rounded-sm transition-colors duration-150"
            >
              <Settings className="w-4 h-4" /> <span className="hidden sm:inline">Hesap</span>
            </NavLink>
            <button
              onClick={handleLogout}
              data-testid="logout-button"
              aria-label="Çıkış yap"
              className="flex items-center gap-1.5 px-2 sm:px-3 py-2 text-sm text-[#8C8377] hover:text-[#A63A3A] hover:bg-[#EFE7D6] rounded-sm transition-colors duration-150"
            >
              <LogOut className="w-4 h-4" /> <span className="hidden sm:inline">Çıkış</span>
            </button>
          </div>
        </div>
      </header>

      {hataOpen && (
        <div className="fixed inset-0 z-[60] bg-black/40 flex items-start justify-center pt-20 px-4" onClick={() => setHataOpen(false)} data-testid="hata-modal">
          <div className="bg-white rounded-sm border border-[#E7DECC] w-full max-w-2xl max-h-[75vh] overflow-y-auto shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-[#E7DECC]">
              <div className="kicker flex items-center gap-1.5 text-[#A63A3A]"><Bug className="w-4 h-4" /> HATA BİLDİR</div>
              <button onClick={() => setHataOpen(false)} data-testid="hata-modal-close" className="text-[#8C8377] hover:text-[#26231E]"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-3">
              <p className="text-sm text-[#8C8377]">Karşılaştığınız sorunu veya önerinizi yazın. Bildiriminiz geliştiriciye iletilir; çözüldüğünde/uygulandığında burada size geri bildirim notu görünür.</p>
              <div className="flex gap-2" data-testid="hata-tur-secim">
                <button type="button" onClick={() => setHataTur("hata")} data-testid="hata-tur-hata" className={`px-3 py-1.5 rounded-sm text-sm font-medium border transition-colors duration-150 ${hataTur === "hata" ? "bg-[#A63A3A] text-white border-[#A63A3A]" : "bg-white text-[#26231E] border-[#E7DECC] hover:bg-[#EFE7D6]"}`}>🐞 Hata</button>
                <button type="button" onClick={() => setHataTur("oneri")} data-testid="hata-tur-oneri" className={`px-3 py-1.5 rounded-sm text-sm font-medium border transition-colors duration-150 ${hataTur === "oneri" ? "bg-[#3B6FB0] text-white border-[#3B6FB0]" : "bg-white text-[#26231E] border-[#E7DECC] hover:bg-[#EFE7D6]"}`}>💡 Öneri</button>
              </div>
              <textarea value={hataMsg} onChange={(e) => setHataMsg(e.target.value)} rows={4} data-testid="hata-mesaj" placeholder={hataTur === "oneri" ? "Örn. Kasa ekranına günlük arama ilerleme çubuğu ekleyelim…" : "Örn. Cari kasa yüklerken şu ekranda hata alıyorum…"} className="w-full px-3 py-2 border border-[#E7DECC] rounded-sm text-sm focus:outline-none focus:border-[#B8933F]" />
              <div className="flex justify-end">
                <button onClick={submitHata} disabled={hataBusy} data-testid="hata-gonder" className="px-4 py-2 bg-[#A63A3A] hover:bg-[#8e3131] text-white rounded-sm text-sm font-medium disabled:opacity-60">Gönder</button>
              </div>

              {!isAdmin && (
                <div className="pt-3 border-t border-[#E7DECC]">
                  <div className="kicker mb-2">BİLDİRDİKLERİM ({hataBenim.length})</div>
                  {hataBenim.length === 0 ? (
                    <p className="text-sm text-[#8C8377]">Henüz bir bildiriminiz yok.</p>
                  ) : (
                    <div className="space-y-2" data-testid="hata-benim-liste">
                      {hataBenim.map((h) => (
                        <div key={h.id} className="border border-[#E7DECC] rounded-sm p-3" data-testid={`hata-benim-${h.id}`}>
                          <div className="flex items-center gap-2 flex-wrap text-xs text-[#8C8377]">
                            {hataTurBadge(h)} {hataDurumBadge(h)}
                            <span>· {h.sayfa || "-"}</span>
                            <span>· {fmtDate(h.created_at)}</span>
                          </div>
                          <div className="text-sm text-[#26231E] mt-1 whitespace-pre-wrap">{h.mesaj}</div>
                          {h.durum === "cozuldu" && (
                            <div className="mt-2 rounded-sm bg-[#2F6E4E]/10 border border-[#2F6E4E]/20 px-2.5 py-1.5" data-testid={`hata-cozum-${h.id}`}>
                              <div className="text-[10px] font-semibold text-[#2F6E4E]">GELİŞTİRİCİ GERİ BİLDİRİMİ{h.cozum_tarihi ? ` · ${fmtDate(h.cozum_tarihi)}` : ""}</div>
                              <div className="text-sm text-[#26231E] mt-0.5 whitespace-pre-wrap">{h.cozum_notu || (h.tur === "oneri" ? "Öneriniz uygulandı." : "Sorun giderildi.")}</div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {isAdmin && (
                <div className="pt-3 border-t border-[#E7DECC]">
                  <div className="kicker mb-2">GELEN BİLDİRİMLER ({hataList.length})</div>
                  {hataList.length === 0 ? (
                    <p className="text-sm text-[#8C8377]">Henüz bildirim yok.</p>
                  ) : (
                    <div className="space-y-2" data-testid="hata-liste">
                      {hataList.map((h) => (
                        <div key={h.id} className={`border rounded-sm p-3 ${h.durum === "cozuldu" ? "border-[#2F6E4E]/30 bg-[#2F6E4E]/5" : "border-[#E7DECC]"}`} data-testid={`hata-item-${h.id}`}>
                          <div className="flex items-center gap-2 flex-wrap text-xs text-[#8C8377]">
                            {hataTurBadge(h)} {hataDurumBadge(h)}
                            <span><b className="text-[#26231E]">{h.user_ad}</b> ({h.user_rol})</span>
                            <span>· {h.sayfa || "-"}</span>
                            <span>· {fmtDate(h.created_at)}</span>
                          </div>
                          <div className="text-sm text-[#26231E] mt-1 whitespace-pre-wrap">{h.mesaj}</div>
                          {h.durum === "cozuldu" ? (
                            <div className="mt-2 rounded-sm bg-[#2F6E4E]/10 border border-[#2F6E4E]/20 px-2.5 py-1.5">
                              <div className="text-[10px] font-semibold text-[#2F6E4E]">GERİ BİLDİRİM · {h.cozen || "-"}{h.cozum_tarihi ? ` · ${fmtDate(h.cozum_tarihi)}` : ""}</div>
                              <div className="text-sm text-[#26231E] mt-0.5 whitespace-pre-wrap">{h.cozum_notu || (h.tur === "oneri" ? "Uygulandı." : "Çözüldü.")}</div>
                            </div>
                          ) : (
                            <div className="mt-2 flex gap-2">
                              <input value={cozumInputs[h.id] || ""} onChange={(e) => setCozumInputs((p) => ({ ...p, [h.id]: e.target.value }))} data-testid={`hata-cozum-input-${h.id}`} placeholder={h.tur === "oneri" ? "Uygulama notu (opsiyonel)…" : "Çözüm notu (opsiyonel)…"} className="flex-1 px-2.5 py-1.5 border border-[#E7DECC] rounded-sm text-sm focus:outline-none focus:border-[#B8933F]" />
                              <button onClick={() => resolveHata(h.id, h.tur)} data-testid={`hata-cozuldu-btn-${h.id}`} className="px-3 py-1.5 bg-[#2F6E4E] hover:bg-[#255a40] text-white rounded-sm text-sm font-medium whitespace-nowrap">{h.tur === "oneri" ? "Yapıldı" : "Çözüldü"}</button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      <div
        className={`fixed inset-0 z-[70] transition-opacity duration-300 ${menuOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"}`}
        aria-hidden={!menuOpen}
      >
        <div
          className="absolute inset-0 bg-black/50 backdrop-blur-sm"
          onClick={() => setMenuOpen(false)}
          data-testid="drawer-backdrop"
        />
        <aside
          className={`absolute left-0 top-0 h-full w-[300px] max-w-[88vw] md:w-[280px] bg-[#FFFDF9] border-r border-[#E7DECC] shadow-2xl flex flex-col transition-transform duration-300 ${menuOpen ? "translate-x-0" : "-translate-x-full"}`}
          data-testid="side-drawer"
        >
          <div className="flex items-center justify-between px-4 h-[57px] border-b border-[#E7DECC] shrink-0">
            <img src="/logo-tek.jpg" alt="Tek Hukuk" className="h-8 w-auto" style={{ mixBlendMode: "multiply" }} />
            <button
              onClick={() => setMenuOpen(false)}
              data-testid="drawer-close"
              aria-label="Menüyü kapat"
              className="p-2 rounded-sm hover:bg-[#EFE7D6] transition-colors duration-150"
            >
              <X className="w-5 h-5 text-[#26231E]" />
            </button>
          </div>
          <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-5" data-testid="drawer-nav">
            {navGroups.map((g, gi) => (
              <div key={gi}>
                {g.title && <div className="kicker px-3 mb-2 text-[#B8933F]">{g.title}</div>}
                <div className="space-y-1.5 md:space-y-1">
                  {g.items.map((item) => (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      end={item.end}
                      onClick={() => setMenuOpen(false)}
                      className={drawerLinkClass}
                      data-testid={item.testid}
                    >
                      <item.icon className="w-5 h-5 md:w-4 md:h-4 shrink-0" />
                      <span className="flex-1">{item.label}</span>
                      {item.badge > 0 && (
                        <span className="min-w-[20px] h-5 md:min-w-[18px] md:h-[18px] px-1.5 md:px-1 rounded-full bg-[#A63A3A] text-white text-xs md:text-[10px] font-bold inline-flex items-center justify-center">
                          {item.badge}
                        </span>
                      )}
                    </NavLink>
                  ))}
                </div>
              </div>
            ))}
          </nav>
          <div className="border-t border-[#E7DECC] px-4 py-3 shrink-0">
            <div className="text-sm font-medium leading-tight">{user?.name}</div>
            <div className="text-xs text-[#8C8377] truncate">{user?.email}</div>
          </div>
        </aside>
      </div>
      <main className="px-4 sm:px-6 py-6 max-w-[1600px] mx-auto">{children}</main>
    </div>
  );
}
